// The chrome's reader for WHICH ORGANISATION the signed-in person is in — the React half of
// tenant/tenantIdentityStore.ts, plus the one request that fills it.
//
// The state, the two writers and the rule for which write wins live in the store, with the fetch injected,
// so that the ordering can be tested without React and without lib/api.ts (whose config.ts reads
// `import.meta.env` and cannot be evaluated outside Vite). Read that file's header for the why of all
// three; this one is the wiring.
//
// WHY IT WAITS FOR THE TENANT (ADR-0018 §6.2). The read is tenant-scoped, and for a person whose token
// carries memberships WHICH tenant is a question they answer after signing in. Asked before that, it would
// answer about whichever tenant the backend resolved from the token alone — their own organisation rather
// than the customer they are about to choose — and the chrome would name the wrong one. So the caller asks
// `useActiveTenantKey()` first (the same wait profile/PreferredLanguageSync.tsx makes, and for the same
// reason) and the hook stores nothing until it has that key.
//
// THE KEY ARRIVES AS AN ARGUMENT rather than being read here, and that is not style: `useActiveTenantKey`
// lives in auth/tenantPicker.tsx, which is the module that RENDERS this value — importing it back would be
// an import cycle between the two, and a cycle whose first evaluation order decides whether a hook exists
// is the kind that fails at runtime and not at the type check. The one caller already holds the key.

import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";
import {
  currentTenantIdentity,
  forgetTenantIdentity,
  loadTenantIdentity,
  subscribeToTenantIdentity,
  type KnownTenantIdentity,
  type TenantOrganisationBranding,
} from "./tenantIdentityStore.ts";

export type { TenantOrganisationBranding };

/**
 * The organisation in force, or null while that is not known — the tenant is still an open question, the
 * read has not answered, or it failed. The chrome draws the name it has from the choice in those states
 * rather than an empty label.
 *
 * Safe to call from more than one component: the read is shared, so they cost one request between them.
 *
 * @param tenantKey `useActiveTenantKey()`'s answer — WHICH ORGANISATION this page's requests are going to,
 * and null while that is still open. Passed in rather than read here; see the header for why.
 */
export function useTenantIdentity(tenantKey: string | null): TenantOrganisationBranding | null {
  const [snapshot, setSnapshot] = useState<KnownTenantIdentity | null>(() => currentTenantIdentity());

  useEffect(() => {
    const unsubscribe = subscribeToTenantIdentity(() => setSnapshot(currentTenantIdentity()));
    // The store may have moved between this component's render and its effect (the settings form publishes
    // from an event handler), so the first thing the subscription does is take the current value.
    setSnapshot(currentTenantIdentity());

    if (tenantKey === null) {
      forgetTenantIdentity();
    } else {
      void loadTenantIdentity(tenantKey, () =>
        apiFetch<TenantOrganisationBranding>("/api/tenant/branding"),
      );
    }

    return unsubscribe;
  }, [tenantKey]);

  // THE KEY IS COMPARED, not merely the presence of a value: between a switch being chosen and its answer
  // arriving, what is held was read for the organisation that was left. Answering it would put one
  // organisation's name over another's rows, which is the whole hazard ADR-0018 §6 exists to remove.
  return snapshot !== null && tenantKey !== null && snapshot.key === tenantKey ? snapshot.branding : null;
}
